import { TriangleAlert } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { getLanguage, t } from '@/i18n';
import type { DashboardSummary } from '@ip/shared-types';
import { formatPLN } from './chart-utils';

interface SummaryCardsProps {
  summary: DashboardSummary;
  topPaymentMethod: string | undefined;
  currentMonthGross: number;
  currentMonthTax: number;
  currentMonthUnpaid: number;
  year: number;
}

interface Tile {
  title: string;
  value: string;
  warning?: boolean;
}

function StatTile({ tile }: { tile: Tile }) {
  return (
    <Card className="py-4">
      <CardContent className="px-4">
        <p className="text-muted-foreground truncate text-xs font-medium" title={tile.title}>
          {tile.title}
        </p>
        <p
          className={`mt-1 flex items-center gap-1.5 font-mono text-lg font-semibold tabular-nums ${
            tile.warning ? 'text-amber-600 dark:text-amber-500' : ''
          }`}
        >
          {tile.warning && <TriangleAlert aria-hidden="true" className="size-4 shrink-0" />}
          <span className="truncate">{tile.value}</span>
        </p>
      </CardContent>
    </Card>
  );
}

export function SummaryCards({
  summary,
  topPaymentMethod,
  currentMonthGross,
  currentMonthTax,
  currentMonthUnpaid,
  year,
}: SummaryCardsProps) {
  const now = new Date();
  const locale = getLanguage() === 'pl' ? 'pl-PL' : 'en-GB';
  const month =
    now.getFullYear() === year ? now.toLocaleString(locale, { month: 'short' }) : '-';
  const money = (v: number) => `${formatPLN(v)} ${summary.currency}`;

  const tiles: Tile[] = [
    { title: t('dashboard.totalCosts'), value: money(summary.totalGross) },
    { title: t('dashboard.totalVat'), value: money(summary.totalTax) },
    { title: t('dashboard.currentMonthGross', { month }), value: money(currentMonthGross) },
    { title: t('dashboard.currentMonthVat', { month }), value: money(currentMonthTax) },
    { title: t('dashboard.invoiceCount'), value: String(summary.invoiceCount) },
    { title: t('dashboard.averageInvoice'), value: money(summary.averageGross) },
    { title: t('dashboard.topPaymentMethod'), value: topPaymentMethod || '-' },
    {
      title: t('dashboard.unpaidTotal'),
      value: money(summary.totalUnpaidGross),
      warning: summary.totalUnpaidGross > 0,
    },
    {
      title: t('dashboard.unpaidCurrentMonth', { month }),
      value: money(currentMonthUnpaid),
      warning: currentMonthUnpaid > 0,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
      {tiles.map((tile) => (
        <StatTile key={tile.title} tile={tile} />
      ))}
    </div>
  );
}
